import { Dispatch, SetStateAction, useContext, useState } from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";

import { DataContext } from "contexts/Data";
import { StreamContext } from "contexts/Stream";
import { ANIMATIONS, DYNAMIC_BASE_URL, MODELS, STREAM_TYPES } from "shared/enum";
import getEnvLoadLevelsObject from "helpers/getEnvLoadLevelsObject";
import useVariant from "hooks/useVariant";
import SessionWarningPopup from "./SessionWarningPopup";
import StreamingErrorPopup from "./StreamingErrorPopup";
import DisconnectedPopup from "./DisconnectedPopup";
import LoadingStream from "./LoadingStream";
import { ReactStream, WasmMethods, getWasmMethods } from "@3ds/3DEXCITEshow-streaming";

type StreamRootProps = {
  shouldUseInitalConfig: boolean;
  setResize: Dispatch<SetStateAction<VoidFunction | undefined>>;
  turntableIsEnabled: boolean;
  isMobile: boolean;
  drawerOpen: boolean;
  sidebarMouseplayTime: number;
  handleLoadScreen: (val: boolean) => void;
};

export default function StreamRoot({
  shouldUseInitalConfig,
  setResize,
  sidebarMouseplayTime,
  handleLoadScreen,
  isMobile,
}: StreamRootProps) {
  const [, setWasmFunctions] = useState<WasmMethods>();
  const variant = useVariant();

  const { cachedState, configuration } = useContext(DataContext);
  const {
    demoState,
    setDemoState,
    scenarios,
    streamingState,
    setStreamingState,
    setTurntableState,
    setStreamViewType,
    streamViewType,
  } = useContext(StreamContext);

  const { selectedCameraView, dropdownChangeSwitch } = demoState;

  const {
    switchStream,
    isStreamStarted,
    isStreamDisconnected,
    desktopMouseSensitivity,
    mobileMoveSensitivity,
    isStreamingAvailable,
  } = streamingState;

  const initialConfiguration = async (wsFunctions) => {
    if (shouldUseInitalConfig && demoState?.variants) {
      // await updatePixelStreamingWithVariants(wsFunctions, demoState.variants);
    }
    // else {
    //   alert("inside else>> stream root");

    // await updatePixelStreamingWithVariants(wsFunctions, variants);
    // }
    const isIngloPlatform = streamViewType === STREAM_TYPES.IN_GLO;
    if (isIngloPlatform) {
      updateIngloPlatformEnvironment(wsFunctions);
    } else {
      await updateEnvironment(wsFunctions);
    }
  };

  const defaultVariants: string[] = variant
    ? [
        configuration.modelDefaults[variant.code].colorId,
        configuration.modelDefaults[variant.code].friendly_name,
      ]
    : [];

  const updateEnvironment = async (wsFunctions: WasmMethods, isRebounceCamera = true) => {
    const isXEV9E = variant.code === MODELS["XEV 9e"].code;

    if (wsFunctions && "loadEnvironmentLevels" in wsFunctions) {
      await wsFunctions.loadEnvironmentLevels(
        JSON.stringify(
          getEnvLoadLevelsObject({
            environmentId: scenarios[0]?.id,
            cameraId: selectedCameraView?.camId,
            dayTime: scenarios[0]?.sun_state.day_time,
            northyaw: scenarios[0]?.sun_state.north_yaw,
            noFade: false,
            applyDaytime: true,
            cameraData: selectedCameraView?.cameraData,
          }),
        ),
      );

      try {
        await wsFunctions.seekAnimation(`${ANIMATIONS[variant.code].goodByeHello.name}`, "100");
      } catch {
        console.warn("goodby animation not working");
      }

      isRebounceCamera &&
        updatePixelStreamingWithVariants(
          wsFunctions,
          /**
           * Commented for now as all variants not required at initial render
           */
          [demoState?.variants, defaultVariants, ...(isXEV9E ? ["DRL_OFF"] : [])].flat(),
          // [defaultVariants].flat(),
        );
    }
  };

  const updateIngloPlatformEnvironment = async (wsFunctions: WasmMethods) => {
    if (wsFunctions && "loadEnvironmentLevels" in wsFunctions) {
      await wsFunctions.loadEnvironmentLevels(
        JSON.stringify(
          getEnvLoadLevelsObject({
            environmentId: scenarios[1]?.id,
            cameraId: selectedCameraView?.camId,
            dayTime: scenarios[1]?.sun_state.day_time,
            northyaw: scenarios[1]?.sun_state.north_yaw,
            noFade: false,
            applyDaytime: true,
            cameraData: selectedCameraView?.cameraData,
          }),
        ),
      );

      try {
        await wsFunctions.seekAnimation(`${ANIMATIONS[variant.code].goodByeHello.name}`, "100");
      } catch {
        console.warn("goodby animation not working");
      }
    }
  };

  const updatePixelStreamingWithVariants = async (
    wsFunctions: WasmMethods,
    changedValue: string | string[],
  ) => {
    try {
      if (wsFunctions && "switchVariants" in wsFunctions) {
        await wsFunctions?.switchVariants(...changedValue);
      }
    } catch (err) {
      console.log(err, " switch variant cordis error");
    }
  };

  const handleRestartStream = () => {
    setStreamingState((prev) => ({
      ...prev,
      isStreamDisconnected: false,
    }));
  };

  return (
    <StyledPage>
      <StyledContainer height="100vh">
        <StreamStyleBox height="100vh" width="100vw">
          {isStreamDisconnected && !switchStream && !isStreamStarted ? (
            <DisconnectedPopup
              handleRestart={() => {
                setStreamingState((prev) => ({
                  ...prev,
                  isStreamStarted: true,
                  isStreamDisconnected: false,
                  switchStream: false,
                }));
              }}
            />
          ) : (
            <ReactStream
              disableRightClickMove
              shouldStream={isStreamingAvailable}
              layoutInteractionTime={sidebarMouseplayTime}
              shouldCloseOnBlur={false}
              modelyear_id={cachedState.model.id}
              errorPopup={(err: string | any) => {
                if (err === "offline") {
                  return <StreamingErrorPopup error={err} />;
                }

                if (
                  err.data === "cordis disconnected" &&
                  err.type === "disconnected" &&
                  !dropdownChangeSwitch
                ) {
                  handleLoadScreen(false);
                  setStreamingState((prev) => ({
                    ...prev,
                    switchStream: false,
                  }));

                  setTurntableState((prev) => ({
                    ...prev,
                    isTurntable: true,
                  }));

                  setStreamViewType(STREAM_TYPES.TURNTABLE);
                }

                if (err === "cordis disconnected" && !dropdownChangeSwitch) {
                  handleLoadScreen(false);
                  setStreamingState((prev) => ({
                    ...prev,
                    switchStream: false,
                  }));

                  setTurntableState((prev) => ({
                    ...prev,
                    isTurntable: true,
                  }));

                  setStreamViewType(STREAM_TYPES.TURNTABLE);
                }

                if (err === "424" || err === "Error in session api response") {
                  handleLoadScreen(false);
                  setStreamingState((prev) => ({
                    ...prev,
                    switchStream: false,
                  }));

                  setTurntableState((prev) => ({
                    ...prev,
                    isTurntable: true,
                  }));

                  setStreamViewType(STREAM_TYPES.TURNTABLE);
                }

                if (err === "lobby:pending") {
                  handleLoadScreen(false);
                  return <LoadingStream />;
                }

                return <></>;

                // !streamingState?.switchStream ? (
                //   <LoadingStream />
                // ) : (
                //   <StreamingErrorPopup error={err} handleRestart={handleRestartStream} />
                // )
              }}
              sessionUpdatePopup={(
                expireTime: number,
                handleSessionContinue,
                handleSessionClose,
              ) => {
                if (expireTime === 1) {
                  setStreamingState((prev) => ({
                    ...prev,
                    switchStream: false,
                  }));

                  setTurntableState((prev) => ({
                    ...prev,
                    isTurntable: true,
                  }));

                  setStreamViewType(STREAM_TYPES.TURNTABLE);
                }
                return (
                  <SessionWarningPopup
                    isMobile={isMobile}
                    expireTime={expireTime}
                    handleSessionContinue={handleSessionContinue}
                    handleSessionClose={() => {
                      if (!streamingState.switchStream) {
                        setTurntableState((prev) => ({
                          ...prev,
                          isTurntable: true,
                        }));

                        setStreamViewType(STREAM_TYPES.TURNTABLE);
                      }
                      handleSessionClose();
                    }}
                  />
                );
              }}
              // initialConfiguration={initialConfiguration}
              onDisconnected={() => {
                setStreamingState((prev) => {
                  // if (!prev.switchStream) {
                  //   setTurntableState((prev) => ({
                  //     ...prev,
                  //     isTurntable: true,
                  //   }));

                  //   setStreamViewType(STREAM_TYPES.TURNTABLE);
                  // }

                  return {
                    ...prev,
                    isStreamDisconnected: true,
                    switchStream: false,
                    // isStreamStarted: prev.switchStream ? true : false,
                    isStreamingAvailable: false,
                  };
                });
              }}
              onConnected={() => {
                setDemoState((prev) => ({
                  ...prev,
                  dropdownChangeSwitch: false,
                }));

                setStreamingState((prev) => ({
                  ...prev,
                  isStreamStarted: true,
                }));

                setStreamViewType(STREAM_TYPES.STREAMING);
                handleLoadScreen(false);

                const wasmMethods = getWasmMethods();
                setWasmFunctions(wasmMethods);
                initialConfiguration(wasmMethods);
              }}
              restart={() => () => {
                setStreamingState((prev) => ({
                  ...prev,
                  isStreamStarted: true,
                }));
              }}
              resize={(resize) => {
                () => resize;
                setResize(() => resize);
              }}
              messagePopup={(overlay) => {
                if (overlay.isStreamingLoading) {
                  handleLoadScreen(overlay.isStreamingLoading);
                }

                if (overlay.isStreamingLoading) {
                  return <></>;
                }
              }}
              fullScreenStream={true}
              wasmFileUrl={DYNAMIC_BASE_URL}
              // onMovement={() => {
              //   updateEnvironment(wasmFunctions, false);
              //   setDemoState((prev) => ({
              //     ...prev,
              //     rebounceCamera: false,
              //   }));
              // }}
              rebounceCamera={demoState.rebounceCamera}
              desktopMouseSensitivity={desktopMouseSensitivity}
              mobileMoveSensitivity={mobileMoveSensitivity}
            />
          )}
        </StreamStyleBox>
      </StyledContainer>
    </StyledPage>
  );
}

type StreamStyleBoxProps = {
  aspectRatio?: string;
};

const StreamStyleBox = styled(Box)<StreamStyleBoxProps>(({ aspectRatio }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  aspectRatio: aspectRatio,
}));

const StyledContainer = styled(Box)(({ theme }) => ({
  boxShadow: theme.shadows[5],
  transition: "height 0.3s ease-in-out",
  borderRadius: theme.shape.borderRadius,
  border: "1px solid rgba(255,255,255,0.05)",
  position: "relative",
  minHeight: "450px",
  overflow: "hidden",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
}));

const StyledPage = styled(Box)(({ theme }) => ({
  maxWidth: "100%",
  height: "100%",
  display: "grid",
  // gridTemplateColumns: '1fr  auto',
  // gridTemplateRows: '1fr auto',
  // gap: theme.spacing(3),
  // padding: theme.spacing(0, 3),
  [theme.breakpoints.down("lg")]: {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr auto auto",
  },
}));
